// src/components/customer/ProductCard.jsx
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FiShoppingCart, FiHeart } from "react-icons/fi";
import { FaStar, FaStarHalfAlt, FaRegStar } from "react-icons/fa";
import { useCart } from "../../context/CartContext";
import { useAuth } from "../../context/AuthContext";
import { addToWishlist, removeFromWishlist, isInWishlist } from "../../services/firestoreService";
import toast from "react-hot-toast";

function StarRating({ rating, reviews }) {
  // No fake numbers: if this product has no real reviews yet, say so
  // instead of showing a made-up 4.8★ / 120 reviews.
  if (!reviews) {
    return (
      <div className="flex items-center justify-center gap-1">
        {[...Array(5)].map((_, i) => <FaRegStar key={i} className="text-gray-200 text-xs sm:text-sm" />)}
        <span className="text-[10px] sm:text-xs text-gray-400 ml-1">No reviews yet</span>
      </div>
    );
  }
  const full  = Math.floor(rating);
  const half  = rating % 1 >= 0.5 ? 1 : 0;
  const empty = 5 - full - half;
  return (
    <div className="flex items-center justify-center gap-1.5 flex-wrap">
      <div className="flex items-center gap-0.5">
        {[...Array(full)].map((_, i)  => <FaStar        key={`f${i}`} className="text-amber-400 text-xs sm:text-sm" />)}
        {half === 1                    && <FaStarHalfAlt              className="text-amber-400 text-xs sm:text-sm" />}
        {[...Array(empty)].map((_, i) => <FaRegStar      key={`e${i}`} className="text-amber-400 text-xs sm:text-sm" />)}
      </div>
      <span className="text-[10px] sm:text-xs text-gray-500">{reviews} review{reviews !== 1 ? "s" : ""}</span>
    </div>
  );
}

export default function ProductCard({ product }) {
  const { addItem } = useCart();
  const { user }    = useAuth();
  const [wishlisted,      setWishlisted]      = useState(false);
  const [wishlistLoading, setWishlistLoading] = useState(false);

  useEffect(() => {
    if (!user) { setWishlisted(false); return; }
    isInWishlist(user.uid, product.id).then(setWishlisted);
  }, [user, product.id]);

  const handleAdd = (e) => {
    e.preventDefault();
    addItem({
      id: product.id,
      name: product.name,
      price: product.price,
      originalPrice: product.originalPrice || null,
      image: product.images?.[0] || "",
      category: product.category || "",
      unit: product.unit,
      weightSize: product.weightSize || null,
      pricePerKg: product.pricePerKg || null,
      packSize: product.packSize || null,
      pricePerPack: product.pricePerPack || null,
      hsnSac: product.hsnSac || "",
    });
    toast.success(`${product.name} added to cart`);
  };

  const handleWishlist = async (e) => {
    e.preventDefault();
    if (!user) { toast.error("Please login to use wishlist"); return; }
    if (wishlistLoading) return;
    setWishlistLoading(true);
    try {
      if (wishlisted) {
        await removeFromWishlist(user.uid, product.id);
        setWishlisted(false);
        toast.success(`${product.name} removed from wishlist`);
      } else {
        await addToWishlist(user.uid, product);
        setWishlisted(true);
        toast.success(`${product.name} added to wishlist ❤️`);
      }
    } catch { toast.error("Something went wrong. Try again."); }
    finally  { setWishlistLoading(false); }
  };

  const mainImage = product.images?.[0];
  const inStock   = product.inStock !== false;
  const hasDiscount = product.originalPrice && Number(product.originalPrice) > Number(product.price);
  const discount  = hasDiscount
    ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
    : 0;

  const weightLabel =
    product.unit === "pack"
      ? (product.packSize   ? `${product.packSize} pack(s)` : null)
      : (product.weightSize ? `${product.weightSize} kg`    : null);

  const rateLabel =
    product.unit === "pack"
      ? (product.pricePerPack ? `₹${product.pricePerPack}/pack` : null)
      : (product.pricePerKg   ? `₹${product.pricePerKg}/kg`    : null);

  return (
    <Link to={`/products/${product.id}`} className="group block h-full">
      <div
        className={`bg-white overflow-hidden transition-all duration-300 h-full flex flex-col
          ${inStock ? "hover:shadow-2xl hover:-translate-y-1" : "opacity-75"}`}
        style={{
          borderRadius: "14px",
          border: "1.5px solid #e0e0cc",
          boxShadow: "0 2px 12px 0 rgba(60,80,30,0.07)",
        }}
      >

        {/* ── IMAGE ── */}
        <div
          className="relative flex-shrink-0 overflow-hidden flex items-center justify-center"
          style={{ aspectRatio: "1 / 1", borderRadius: "12px 12px 0 0", background: "#fafaf5" }}
        >
          {mainImage ? (
            <img
              src={mainImage}
              alt={product.name}
              className="w-full h-full object-contain p-3 transition-transform duration-500 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-gray-50 flex items-center justify-center text-5xl sm:text-7xl">🌱</div>
          )}

          {/* Stock badge – top-right, same pill design as the in-stock indicator below */}
          <span
            className={`absolute top-2 right-2 text-[10px] sm:text-xs font-semibold px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-full z-10 whitespace-nowrap
              ${inStock ? "bg-green-50 text-green-700" : "bg-red-50 text-red-600"}`}
          >
            {inStock ? "In Stock" : "Out of Stock"}
          </span>

          {/* Wishlist – top-left */}
          <button
            onClick={handleWishlist}
            disabled={wishlistLoading}
            className="absolute top-2 left-2 w-7 h-7 sm:w-8 sm:h-8 rounded-full bg-white/90 backdrop-blur-sm shadow flex items-center justify-center hover:scale-110 transition disabled:opacity-60 z-10"
          >
            <FiHeart className={`text-xs sm:text-sm transition-colors ${wishlisted ? "text-red-500 fill-red-500" : "text-gray-400"}`} />
          </button>

          {/* Weight pill – bottom-right over image */}
          {weightLabel && (
            <div
              className="absolute bottom-2 right-2 text-white text-[9px] sm:text-[11px] font-bold px-2 sm:px-3 py-0.5 sm:py-1 z-10"
              style={{ background: "rgba(0,0,0,0.55)", borderRadius: "999px", backdropFilter: "blur(4px)" }}
            >
              {weightLabel}
            </div>
          )}

          {/* Out of stock overlay */}
          {!inStock && (
            <div className="absolute inset-0 bg-white/70 flex items-center justify-center z-10">
              <span className="text-xs font-semibold text-gray-700 bg-white px-3 py-1 rounded-full shadow">
                Out of stock
              </span>
            </div>
          )}
        </div>

        <div className="px-3 pt-3 pb-3 sm:px-4 sm:pt-4 sm:pb-4 flex flex-col flex-1 items-center text-center gap-1 sm:gap-1.5">

          {/* Star rating */}
          <StarRating rating={product.rating || 0} reviews={product.reviews || 0} />

          <div className="w-full flex items-center justify-center mt-0.5 sm:mt-1">
            <h3
              className="font-bold text-gray-900 text-[11px] sm:text-sm uppercase leading-snug tracking-wide group-hover:text-green-700 transition-colors"
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {product.name}
            </h3>
          </div>

          {/* Price row — always same content, no layout shift */}
          <div className="flex items-center justify-center gap-1.5 sm:gap-2 flex-wrap w-full mt-1">
            {hasDiscount && (
              <span className="text-[11px] sm:text-sm text-gray-400 line-through font-medium">
                ₹{product.originalPrice}/-
              </span>
            )}
            <span className="text-sm sm:text-base font-extrabold text-gray-900">
              ₹{product.price}/-
            </span>
          </div>

          {/* Discount badge — space reserved even when there's no discount, so every card lines up */}
          {/* <div className="h-[18px] sm:h-[20px] flex items-center justify-center w-full">
            {hasDiscount && (
              <span
                className="text-[9px] sm:text-[11px] font-extrabold text-white px-1.5 sm:px-2 py-0.5 rounded-full whitespace-nowrap"
                style={{ background: "#4a6e30" }}
              >
                {discount}% OFF
              </span>
            )}
          </div> */}

          {/* Rate/pack sub-caption — space reserved even when absent */}
          <div className="h-[14px] sm:h-[16px] flex items-center justify-center w-full">
            {rateLabel && (
              <p className="text-[9px] sm:text-[10px] text-gray-400 whitespace-nowrap">{rateLabel}</p>
            )}
          </div>

          <button
            onClick={handleAdd}
            disabled={!inStock}
            className="mt-auto w-full flex items-center justify-center gap-1.5 sm:gap-2 py-2 sm:py-2.5 font-extrabold text-[10px] sm:text-xs uppercase tracking-widest transition-all duration-200 mt-2 sm:mt-3"
            style={{
              borderRadius: "8px",
              background: inStock ? "#3a5c2f" : "#d1d5db",
              color: inStock ? "#fff" : "#9ca3af",
              cursor: inStock ? "pointer" : "not-allowed",
              letterSpacing: "0.12em",
            }}
          >
            <FiShoppingCart className="text-xs sm:text-sm flex-shrink-0" />
            {inStock ? "Add to Cart" : "Out of Stock"}
          </button>

        </div>
      </div>
    </Link>
  );
}